import { createAction } from '@reduxjs/toolkit';
import { User } from '../auth/actions';
import { Task } from '../tasks/actions';

export interface Store {
    id: number;
    modifed_date: string;
    title: string;
    location: string;
    rent: number;
    capacity: number;
    added_at: string;
}

export interface Provider {
    id: number;
    modifed_date: string;
    name: string;
}

export interface Part {
    id?: number;
    store?: Store;
    store_id?: number;
    thumbnail?: Thumbnail;
    status?: string;
    display_status?: string;
    provider?: Provider;
    provider_id?: number;
    used_parts?: Part[];
    modifed_date?: string;
    name?: string;
    number?: string;
    cost?: number;
    invoice?: string | null;
    date_used?: string | null;
    delivery_company?: string;
    delivery_order?: string;
    delivery_cost?: number | null;
    delivery_date?: string | null;
    delivery_est_date?: string | null;
    delivery_pickup_date?: string | null;
    delivery_to?: string | null;
    delivery_from?: string | null;
    vehicle?: Vehicle | null;
    signature?: string | null;
    qty?: string | null;
    note?: string;
    vehicle_id?: number;
}

export interface Thumbnail {
    id: number;
    type: string;
    is360: boolean;
    is_use: boolean;
    full_url: string;
    modifed_date: string;
}

export interface Media {
    id: number;
    type: string;
    section: MediaType;
    is_use: boolean;
    full_url: string;
    modifed_date: string;
    vehicle_id?: number;
    vehicle?: Vehicle;
}

export interface Document {
    id: number;
    type: string;
    filename: string;
    file: string;
    modifed_date: string;
    vehicle_id?: number;
    vehicle?: Vehicle;
    type_display?: string;
}

export interface InspectionResults {
    [key: string]: {
        [key: number]: number;
    };
}

export interface Vehicle {
    [key: string]: any;
    id?: number;
    store?: Store;
    floorplan?: string | null;
    store_id?: number;
    floorplan_id?: number | null;
    ymmt?: string;
    display_status?: string;
    rating?: number;
    num_reviews?: number;
    days_owned?: number;
    rent_price?: number;
    floorplan_cost?: number;
    profit?: number;
    parts?: Part[];
    thumbnail?: Thumbnail;
    medias?: Media[] | null;
    medias_360?: Media[] | null;
    modifed_date?: string;
    market?: string;
    payment_method?: string;
    purchase_price?: number;
    delivery_cost?: number | null;
    reserve_price?: number | null;
    sale_price?: number | null;
    sale_method?: string;
    status?: string;
    is_purchased?: boolean;
    is_listed?: boolean;
    is_reserved?: boolean;
    is_sold?: boolean;
    held_at?: string | null;
    vin?: string;
    year?: number;
    make?: string;
    model?: string;
    parts_budget?: number;
    labor_budget?: number;
    parts_cost?: number;
    labor_cost?: number;
    est_labor_cost?: number;
    trim?: string;
    miles?: number;
    title_code?: string;
    body_style?: string;
    drive_type?: string;
    fuel_type?: string;
    engine_type?: string;
    transmission?: string;
    color?: string;
    prior_use?: string;
    horse_power?: number | null;
    city_mpg?: number | null;
    hwy_mpg?: number | null;
    features?: string[];
    about?: string;
    history?: string;
    inspection?: string;
    inspection_results?: InspectionResults;
    inspection_score?: number;
    signed_by?: User | null;
    delivery_from?: string | null;
    delivery_to?: string | null;
    delivery_company?: string | null;
    delivery_order?: string | null;
    delivery_date?: string | null;
    delivery_est_date?: string | null;
    delivery_pickup_date?: string | null;
    purchase_date?: string | null;
    mechanic_fix_date?: string | null;
    body_fix_date?: string | null;
    sale_ready_date?: string | null;
    sale_date?: string | null;
    signature?: string | null;
    total_expenses?: number;
    estimated_sale_price?: number;
    documents?: Document[];
    tasks?: Task[];
    sale_commission?: {
        type: string;
        commission: number;
    };
}

export interface MediaType {
    id: number;
    name: string;
    is_in: boolean;
    is_out: boolean;
    is_keypoint: boolean;
    icon?: string;
}

export interface InspectionOption {
    id: number;
    title: string;
    score: number | null;
}

export interface InspectionQuestion {
    id: number;
    title: string;
    options: InspectionOption[];
    modifed_date: string;
}

export interface Inspection {
    id: number;
    title: string;
    type: number;
    subtitle: string;
    questions: InspectionQuestion[];
    modifed_date: string;
}

export interface InspectionSection {
    id: number;
    title: string;
    inspections: Inspection[];
    modifed_date: string;
}

export const listVehiclesRequest = createAction<{filters: string, vehicle_fields: string, excludes: string, save_as?: string}>('vehicles/listRequest');
export const listVehiclesSuccess = createAction<{vehicles: Vehicle[], save_as?: string}>('vehicles/listSuccess');
export const listVehiclesFailure = createAction<string>('vehicles/listFailure');

export const getVehicleRequest = createAction<{id?: number, vin?: string, vehicle_fields: string}>('vehicles/getRequest');
export const setVehicleRequest = createAction<Vehicle|null>('vehicles/setRequest');
export const getVehicleSuccess = createAction<Vehicle|null>('vehicles/getSuccess');
export const getVehicleFailure = createAction<string>('vehicles/getFailure');

export const updateVehicleRequest = createAction<{vehicle: Vehicle, update_fields: string[], vehicle_fields?: string, cb?: any}>('vehicles/updateRequest');
export const updateVehicleSuccess = createAction<Vehicle>('vehicles/updateSuccess');
export const updateVehicleFailure = createAction<string>('vehicles/updateFailure');

export const uploadMediaRequest = createAction<{vehicle_id: number, section_id: number, files: FileList}>('vehicles/uploadMediaRequest');
export const uploadMediaSuccess = createAction<{vehicle_id: number, medias: Media[]}>('vehicles/uploadMediaSuccess');
export const uploadMediaFailure = createAction<string>('vehicles/uploadMediaFailure');

export const getStoreLocationsRequest = createAction('vehicles/getStoreLocationsRequest');
export const getStoreLocationsSuccess = createAction<Store[]>('vehicles/getStoreLocationsSuccess');
export const getStoreLocationsFailure = createAction<string>('vehicles/getStoreLocationsFailure');

export const getInspectionsRequest = createAction('vehicles/getInspectionsRequest');
export const getInspectionsSuccess = createAction<InspectionSection[]>('vehicles/getInspectionsSuccess');
export const getInspectionsFailure = createAction<string>('vehicles/getInspectionsFailure');

export const uploadDocumentRequest = createAction<{vehicle_id: number, files: FileList, type: string, filenames: string[]}>('vehicles/uploadDocumentRequest');
export const uploadDocumentSuccess = createAction<{vehicle_id: number, documents: Document[]}>('vehicles/uploadDocumentSuccess');
export const uploadDocumentFailure = createAction<string>('vehicles/uploadDocumentFailure');

export const getMediaTypesRequest = createAction('vehicles/getMediaTypesRequest');
export const getMediaTypesSuccess = createAction<MediaType[]>('vehicles/getMediaTypesSuccess');
export const getMediaTypesFailure = createAction<string>('vehicles/getMediaTypesFailure');

export const listVehiclesDeliveryRequest = createAction<{vehicle_fields: string, date_from?: string, date_to?: string}>('vehicles/listDeliveryRequest');
export const listVehiclesDeliverySuccess = createAction<Vehicle[]>('vehicles/listDeliverySuccess');
export const listVehiclesDeliveryFailure = createAction<string>('vehicles/listDeliveryFailure');

export const resetErrorRequest = createAction('vehicles/resetErrorRequest');
